import React from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuthorization, type OrgRole } from '../hooks/use-authorization';

import { usePermissions } from '~/client/contexts/permissions-context';

interface Props extends React.PropsWithChildren {
  className?: string;
  /** Organization roles required — user must have at least one */
  orgRoles?: OrgRole[];
  /** Require system admin privileges */
  requireSystemAdmin?: boolean;
  /** Require beta user access */
  requireBeta?: boolean;
  /** Shorthand for orgRoles={['ORG_ADMIN']} */
  requireOrgAdmin?: boolean;
  /** Allow any elevated org role (ORG_ADMIN, COMPLIANCE_MANAGER, AUDITOR) */
  requireElevatedOrgRole?: boolean;
  /** Whether to redirect to sign-in if access is denied. Default: true */
  allowRedirect?: boolean;
}

/**
 * FeatureAuth Component
 *
 * Enhanced authorization component that supports:
 * - Organization-level role checks
 * - Platform-level system admin checks
 * - Beta feature access checks
 *
 * This replaces the legacy RolesAuthRoles component with a more flexible
 * authorization system that works with the multi-organization architecture.
 *
 * @example
 * // Beta feature access
 * <FeatureAuth requireBeta={true}>
 *   <BetaFeatureComponent />
 * </FeatureAuth>
 *
 * @example
 * // Organization admin only
 * <FeatureAuth requireOrgAdmin={true}>
 *   <AdminPanel />
 * </FeatureAuth>
 *
 * @example
 * // System admin only
 * <FeatureAuth requireSystemAdmin={true}>
 *   <SystemAdminDashboard />
 * </FeatureAuth>
 *
 * @example
 * // Combined: Beta users OR org admins
 * <FeatureAuth requireBeta={true} orgRoles={['ORG_ADMIN']}>
 *   <ExperimentalFeature />
 * </FeatureAuth>
 */
const FeatureAuth: React.FC<Props> = ({
  children,
  orgRoles,
  requireSystemAdmin = false,
  requireBeta = false,
  requireOrgAdmin = false,
  requireElevatedOrgRole = false,
  allowRedirect = true,
}) => {
  const auth = useAuthorization();
  const { hasCapability } = usePermissions();
  const navigate = useNavigate();

  const hasAccess = React.useMemo(() => {
    if (!requireBeta && hasCapability('platform:manage_system_admins')) {
      return true;
    }

    const checks: boolean[] = [];

    if (requireSystemAdmin) {
      checks.push(hasCapability('platform:manage_system_admins'));
    }

    if (requireBeta) {
      checks.push(auth.isBetaUser());
    }

    if (requireOrgAdmin) {
      checks.push(hasCapability('org:manage_members'));
    }

    if (requireElevatedOrgRole) {
      checks.push(auth.hasElevatedOrgRole());
    }

    if (orgRoles && orgRoles.length > 0) {
      checks.push(auth.hasOrgRole(orgRoles));
    }

    if (checks.length === 0) {
      return auth.isAuthenticated();
    }
    return checks.some((check) => check === true);
  }, [
    auth,
    hasCapability,
    requireSystemAdmin,
    requireBeta,
    requireOrgAdmin,
    requireElevatedOrgRole,
    orgRoles,
  ]);

  // Handle unauthorized access
  if (!hasAccess) {
    if (allowRedirect) {
      navigate('/sign-in');
    }
    return null;
  }

  return <>{children}</>;
};

export default FeatureAuth;
